"use client"

import { useState, useRef } from "react"
import styled from "styled-components"
import { User, Image, Tag, Send, Video, MapPin, Hash, X } from "lucide-react"
import { useTheme } from "../context/ThemeContext"

const Container = styled.div`
  background-color: ${props => props.theme.cardBackground};
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
  overflow: hidden;
  margin-bottom: 20px;
  cursor: ${props => props.$isLoggedIn ? 'default' : 'pointer'};
  transition: all 0.2s ease;
  
  ${props => !props.$isLoggedIn && `
    &:hover {
      box-shadow: ${props.theme.shadowHover};
      transform: translateY(-1px);
    }
  `}
`

const Content = styled.div`
  padding: 16px;
  display: flex;
  gap: 12px;
`

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${props => props.theme.borderLight};
  flex-shrink: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const TextareaWrapper = styled.div`
  flex: 1;
`

const Textarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  border: none;
  resize: none;
  font-size: 14px;
  outline: none;
  background: transparent;
  color: ${props => props.theme.textPrimary};

  &::placeholder {
    color: ${props => props.theme.textMuted};
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  background-color: ${props => props.theme.hoverBackground};
  border-top: 1px solid ${props => props.theme.border};
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 4px;
  border: none;
  background-color: transparent;
  color: ${props => props.theme.textSecondary};
  font-size: 13px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.7 : 1};

  &:hover {
    background-color: ${props => props.disabled ? 'transparent' : props.theme.hoverBackground};
  }

  @media (max-width: 576px) {
    padding: 6px;
    
    span {
      display: none;
    }
  }
`

const PostButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  border-radius: 4px;
  border: none;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.buttonPrimaryHover};
  }

  &:disabled {
    background-color: ${props => props.theme.buttonSecondary};
    cursor: not-allowed;
  }
`

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// 이미지 미리보기를 위한 스타일 컴포넌트
const ImagePreviewContainer = styled.div`
  margin: 0 16px 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ImagePreviewItem = styled.div`
  position: relative;
  width: 100px;
  height: 100px;
  overflow: hidden;
  border-radius: 4px;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveImageButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
`;

// 태그 관련 스타일 컴포넌트
const TagContainer = styled.div`
  margin: 0 16px 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TagItem = styled.div`
  display: flex;
  align-items: center;
  background-color: ${props => props.theme.buttonPrimary};
  color: white;
  padding: 4px 8px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
`;

const TagRemoveButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
  }
`;

const TagInputContainer = styled.div`
  margin: 0 16px 12px;
  padding: 12px;
  background-color: ${props => props.theme.hoverBackground};
  border-radius: 8px;
`;

const TagInput = styled.input`
  width: 100%;
  border: none;
  background: none;
  outline: none;
  font-size: 14px;
  color: ${props => props.theme.textPrimary};
  
  &::placeholder {
    color: ${props => props.theme.textMuted};
  }
`;

export default function PostCreator({ user, isLoggedIn, onPostCreated, onLoginRequired }) {
  const { theme } = useTheme()
  const [content, setContent] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [uploadedImages, setUploadedImages] = useState([])
  const [tags, setTags] = useState([])
  const [currentTag, setCurrentTag] = useState("")
  const [showTagInput, setShowTagInput] = useState(false)
  const fileInputRef = useRef(null)
  const tagInputRef = useRef(null)

  const handleImageClick = () => {
    if (!isLoggedIn) {
      alert('이미지 업로드는 로그인 후 이용 가능합니다.');
      return;
    }
    fileInputRef.current.click()
  }

  const handleFileChange = async (e) => {
    if (!isLoggedIn) return;
    
    const file = e.target.files[0];
    if (!file) return;
    
    // 파일 유형 확인
    if (!file.type.includes('image/')) {
      alert('이미지 파일만 업로드 가능합니다.');
      return;
    }
    
    // 파일 크기 제한 (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('파일 크기는 5MB 이하여야 합니다.');
      return;
    }
    
    try {
      // 파일 업로드
      const formData = new FormData();
      formData.append('files', file);
      
      // 파일 서버에 업로드
      const uploadResponse = await fetch('https://file.skynet.re.kr/awsUpload/send?folder=story_antsnest', {
        method: 'POST',
        body: formData,
      });
      
      const uploadData = await uploadResponse.json();
      console.log("파일 업로드 응답:", uploadData);
      
      // 응답 구조에 맞게 조건 수정
      if (uploadData.message === "Multiple files uploaded successfully" && 
          uploadData.files && 
          uploadData.files.length > 0) {
        
        const imageUrl = uploadData.files[0].url;
        const fileName = uploadData.files[0].fileName;
        
        // 최대 4개 이미지로 제한
        if (uploadedImages.length >= 4) {
          alert('이미지는 최대 4개까지 업로드 가능합니다.');
          return;
        }
        
        // 업로드된 이미지 목록에 추가
        setUploadedImages([...uploadedImages, {
          url: imageUrl,
          caption: fileName
        }]);
      } else {
        console.error("이미지 업로드 실패:", uploadData);
        alert('이미지 업로드에 실패했습니다.');
      }
    } catch (error) {
      console.error("이미지 업로드 오류:", error);
      alert('이미지 업로드 중 오류가 발생했습니다.');
    }
  }

  const handleRemoveImage = (index) => {
    if (!isLoggedIn) return;
    
    const newImages = [...uploadedImages];
    newImages.splice(index, 1);
    setUploadedImages(newImages);
  }

  // 태그 관련 함수들
  const handleTagClick = () => {
    if (!isLoggedIn) {
      alert('태그 추가는 로그인 후 이용 가능합니다.');
      return;
    }
    setShowTagInput(true);
    setTimeout(() => {
      if (tagInputRef.current) {
        tagInputRef.current.focus();
      }
    }, 100);
  }

  const handleTagInputChange = (e) => {
    setCurrentTag(e.target.value);
  }

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Escape') {
      setShowTagInput(false);
      setCurrentTag("");
    }
  }

  const addTag = () => {
    const tagText = currentTag.trim().replace(/^#/, ''); // # 제거
    if (tagText && !tags.includes(tagText) && tags.length < 5) {
      setTags([...tags, tagText]);
    }
    setCurrentTag("");
    setShowTagInput(false);
  }

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  }

  const handleSubmit = async () => {
    if (!isLoggedIn) {
      if (onLoginRequired) {
        onLoginRequired();
      }
      return;
    }
    
    if ((!content.trim() && uploadedImages.length === 0) || isSubmitting) return
    
    setIsSubmitting(true)
    
    try {
      // 게시물 데이터 준비
      const postData = {
        content,
        images: uploadedImages,
        tags: tags
      }
      
      // API 호출하여 게시물 저장
      const response = await fetch('/post/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(postData)
      })
      
      const data = await response.json()
      
      if (data.success) {
        console.log("게시물 작성 성공:", data.post)
        
        // 입력 필드 초기화
        setContent("")
        setUploadedImages([])
        setTags([])
        
        // 성공 메시지 표시
        alert("게시물이 성공적으로 작성되었습니다!")
        
        // 부모 컴포넌트에 알림 (게시물 목록 갱신용)
        if (onPostCreated) {
          onPostCreated(data.post)
        }
      } else {
        console.error("게시물 작성 실패:", data.message)
        alert("게시물 작성에 실패했습니다: " + data.message)
      }
    } catch (error) {
      console.error("게시물 작성 오류:", error)
      alert("게시물 작성 중 오류가 발생했습니다.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleContainerClick = () => {
    if (!isLoggedIn && onLoginRequired) {
      onLoginRequired();
    }
  };

  return (
    <Container 
      theme={theme} 
      $isLoggedIn={isLoggedIn}
      onClick={handleContainerClick}
    >
      <Content>
        <Avatar theme={theme}>
          {isLoggedIn && user?.avatar_url ? (
            <AvatarImage src={user.avatar_url} alt="프로필 이미지" />
          ) : (
            <User size={24} />
          )}
        </Avatar>
        <TextareaWrapper>
          <Textarea
            theme={theme}
            placeholder={isLoggedIn ? "무슨 생각을 하고 계신가요?" : "게시물을 작성하려면 로그인이 필요합니다."}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            disabled={!isLoggedIn}
            style={!isLoggedIn ? { cursor: 'pointer' } : {}}
          />
        </TextareaWrapper>
      </Content>
      
      {/* 이미지 미리보기 영역 - 로그인 상태일 때만 보여줌 */}
      {isLoggedIn && uploadedImages.length > 0 && (
        <ImagePreviewContainer>
          {uploadedImages.map((image, index) => (
            <ImagePreviewItem key={index}>
              <PreviewImage src={image.url} alt={`업로드 이미지 ${index + 1}`} />
              <RemoveImageButton onClick={() => handleRemoveImage(index)}>
                <X size={12} />
              </RemoveImageButton>
            </ImagePreviewItem>
          ))}
        </ImagePreviewContainer>
      )}
      
      {/* 태그 표시 영역 */}
      {isLoggedIn && tags.length > 0 && (
        <TagContainer>
          {tags.map((tag, index) => (
            <TagItem key={index} theme={theme}>
              #{tag}
              <TagRemoveButton onClick={() => removeTag(tag)}>
                <X size={12} />
              </TagRemoveButton>
            </TagItem>
          ))}
        </TagContainer>
      )}

      {/* 태그 입력 영역 */}
      {isLoggedIn && showTagInput && (
        <TagInputContainer theme={theme}>
          <TagInput
            theme={theme}
            ref={tagInputRef}
            value={currentTag}
            onChange={handleTagInputChange}
            onKeyDown={handleTagInputKeyPress}
            onBlur={() => {
              if (currentTag.trim()) {
                addTag();
              } else {
                setShowTagInput(false);
              }
            }}
            placeholder="태그 입력 (최대 5개)"
            maxLength={20}
          />
        </TagInputContainer>
      )}
      
      <Footer theme={theme}>
        <Actions>
          <ActionButton theme={theme} onClick={handleImageClick} disabled={!isLoggedIn}>
            <Image size={18} />
            <span>사진</span>
            <input
              ref={fileInputRef}
              type="file"
              name="files"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              disabled={!isLoggedIn}
            />
          </ActionButton>
          <ActionButton theme={theme} disabled={!isLoggedIn}>
            <Video size={18} />
            <span>동영상</span>
          </ActionButton>
          <ActionButton theme={theme} disabled={!isLoggedIn}>
            <MapPin size={18} />
            <span>위치</span>
          </ActionButton>
          <ActionButton theme={theme} onClick={handleTagClick} disabled={!isLoggedIn}>
            <Hash size={18} />
            <span>태그</span>
          </ActionButton>
        </Actions>
        <PostButton 
          theme={theme}
          disabled={!isLoggedIn || (!content.trim() && uploadedImages.length === 0) || isSubmitting} 
          onClick={handleSubmit}
        >
          <Send size={16} />
          <span>{isSubmitting ? "게시 중..." : "게시하기"}</span>
        </PostButton>
      </Footer>
    </Container>
  )
}